"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { api, humanMessage } from "../api";
import { useManagedSeller, type SellerAccess } from "../seller";
import type { Franchise, Paged, SellerMember, SellerSummary, SettlementEntry, SettlementPayout } from "../types";
import { useAsync, type Async } from "../useAsync";
import { fieldErrorsOf } from "./shared";

/*
 * 판매처 콘솔 (총판·가맹점 관리자 — 몰 안의 `/seller/{id}/*`).
 *
 * 사용:
 *   const access = useSellerConsole(params.id);          // { state: loading|denied|ok, seller, sellerId }
 *   const summary = useSellerSummary(access);            // summary.data: SellerSummary | null
 *   const franchises = useFranchises(access);            // 총판만 부릅니다 · data: Franchise[] | null
 *   const payouts = useSellerPayouts(access, { page });  // data: Paged<SettlementPayout> | null
 *   const entries = useSettlementEntries(access, { month: "2026-09", page });
 *   const members = useSellerMembers(access, { query, page });
 *   const create = useCreateFranchise(access);           // await create.create({ code, name, ratePercent, … })
 *   const rate = useFranchiseRate(access, childId);      // await rate.change(ratePercent, reason)
 *
 * 🔴 **여기서 막는 것은 «보이지 않게» 뿐입니다.** 진짜 판정은 서버가 라우트마다 합니다
 *    (`EnsureSellerManager`). `access.state === "ok"` 가 되기 전에는 아무것도 부르지 않습니다.
 * 🔑 요율은 화면에서 퍼센트, 서버와는 bp(100 = 1%)입니다 — 변환은 이 훅이 합니다.
 */

/** 판매처 영역에 들어갈 수 있는가 (`_lib/seller.ts` 의 `useManagedSeller` 와 같습니다). */
export function useSellerConsole(rawId: string): SellerAccess {
  return useManagedSeller(rawId);
}

const isDistributor = (access: SellerAccess) => access.state === "ok" && access.seller.kind === "distributor";

export function useSellerSummary(access: SellerAccess): Async<SellerSummary> {
  return useAsync(
    (signal) => api.get<SellerSummary>(`/sellers/${access.sellerId}/summary`, { signal }),
    `seller-summary-${access.sellerId}`,
    access.state === "ok",
  );
}

/** 🔴 총판만 — 가맹점은 자기 아래에 판매처를 두지 못합니다(단계 상한). 가맹점이면 부르지 않습니다. */
export function useFranchises(access: SellerAccess): Async<Franchise[]> {
  return useAsync(
    async (signal) => (await api.get<{ items: Franchise[] }>(`/sellers/${access.sellerId}/franchises`, { signal })).items,
    `seller-franchises-${access.sellerId}`,
    isDistributor(access),
  );
}

export function useSellerPayouts(
  access: SellerAccess,
  options: { page?: number; perPage?: number; enabled?: boolean } = {},
): Async<Paged<SettlementPayout>> {
  const page = options.page ?? 1;
  return useAsync(
    (signal) =>
      api.get<Paged<SettlementPayout>>(`/sellers/${access.sellerId}/settlements/payouts`, {
        query: { page, per_page: options.perPage },
        signal,
      }),
    `seller-payouts-${access.sellerId}-${page}-${options.perPage ?? ""}`,
    access.state === "ok" && options.enabled !== false,
  );
}

/** 🔴 취소가 나면 반대 항목(reversal)이 **마이너스로** 쌓입니다 — 원장은 지우지 않습니다. */
export function useSettlementEntries(
  access: SellerAccess,
  options: { month: string; page?: number; enabled?: boolean },
): Async<Paged<SettlementEntry>> {
  const page = options.page ?? 1;
  return useAsync(
    (signal) =>
      api.get<Paged<SettlementEntry>>(`/sellers/${access.sellerId}/settlements/entries`, {
        query: { month: options.month, page },
        signal,
      }),
    `seller-entries-${access.sellerId}-${options.month}-${page}`,
    access.state === "ok" && options.enabled !== false,
  );
}

/** 🔴 이메일은 **서버가 가려서** 줍니다(`emailMasked`). 총판이 보면 `franchise` 가 붙습니다. */
export function useSellerMembers(access: SellerAccess, options: { query?: string; page?: number } = {}): Async<Paged<SellerMember>> {
  const query = options.query ?? "";
  const page = options.page ?? 1;
  return useAsync(
    (signal) =>
      api.get<Paged<SellerMember>>(`/sellers/${access.sellerId}/members`, {
        query: { q: query || undefined, page },
        signal,
      }),
    `seller-members-${access.sellerId}-${query}-${page}`,
    access.state === "ok",
  );
}

/** 퍼센트 문자열(`"1.5"`) → bp. 비었으면 `undefined`(서버 기본값). */
function percentToBp(value: string): number | undefined {
  return value.trim() ? Math.round(Number(value) * 100) : undefined;
}

export type NewFranchiseInput = {
  code: string;
  name: string;
  /** 퍼센트 문자열. 비우면 서버 기본 요율 */
  ratePercent?: string;
  contactName?: string;
  contactPhone?: string;
};

/**
 * 가맹점 만들기 — 🔴 총판만. 요율은 우리 요율을 넘을 수 없고 서버가 422 로 거부합니다.
 * 성공하면 `router.replace` 로 가맹점 목록에 갑니다.
 */
export function useCreateFranchise(access: SellerAccess) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});

  async function create(input: NewFranchiseInput): Promise<void> {
    if (busy) return;

    setBusy(true);
    setError("");
    setFields({});

    try {
      await api.post(`/sellers/${access.sellerId}/franchises`, {
        code: input.code.trim(),
        name: input.name.trim(),
        rate_bp: percentToBp(input.ratePercent ?? ""),
        contact_name: input.contactName?.trim() || undefined,
        contact_phone: input.contactPhone?.trim() || undefined,
      });
      router.replace(`/seller/${access.sellerId}/franchises`);
    } catch (cause) {
      setFields(fieldErrorsOf(cause, ["code", "name", "rate_bp", "contact_name", "contact_phone"]));
      setError(humanMessage(cause));
      setBusy(false);
    }
  }

  return { create, busy, error, fields };
}

/**
 * 가맹점 요율 바꾸기 — 🔴 총판만 · **사유 필수**(기록에 남습니다 · `seller_rate_changes`).
 * 🔴 바꾼 요율은 앞으로의 판매부터입니다 — 원장은 판매 시점 요율 스냅숏이라 지난 정산은 그대로입니다.
 */
export function useFranchiseRate(access: SellerAccess, childId: string) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});

  async function change(ratePercent: string, reason: string): Promise<void> {
    if (busy) return;

    setBusy(true);
    setError("");
    setFields({});

    try {
      await api.patch(`/sellers/${access.sellerId}/franchises/${encodeURIComponent(childId)}/rate`, {
        rate_bp: Math.round(Number(ratePercent) * 100),
        reason: reason.trim(),
      });
      router.replace(`/seller/${access.sellerId}/franchises`);
    } catch (cause) {
      setFields(fieldErrorsOf(cause, ["rate_bp", "reason"]));
      setError(humanMessage(cause));
      setBusy(false);
    }
  }

  return { change, busy, error, fields };
}
